/**
 * Once-per-event localStorage gate, originally built for per-icon
 * first-click NIPE bubbles. The per-icon bubble catalog was removed
 * 2026-05-27 — it duplicated the FirstRunTour (icon click fired a
 * stale short-form bubble on top of the tour's longer copy).
 *
 * The storage helpers stay because portal-welcome (the sticky
 * customer-provisioned greeting at Win95Desktop.tsx) reuses the same
 * STORAGE_KEY with synthetic event keys like `welcome:onboarding:<slug>`.
 *
 * Keep this file thin and storage-only. If you find yourself adding
 * per-icon copy back here, it belongs in FirstRunTour's TOUR_STEPS
 * instead — single source of truth.
 */

const STORAGE_KEY = 'repancy-nipe-first-click-seen';

/** Read seen-set from localStorage. Defensive against private mode. */
function readSeen(): Set<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return new Set();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((p): p is string => typeof p === 'string'));
  } catch {
    return new Set();
  }
}

function writeSeen(seen: Set<string>): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify([...seen]));
  } catch {
    /* private-mode / quota — ignore */
  }
}

/** Has the user already seen the event for this key? */
export function hasSeenFirstClick(path: string): boolean {
  return readSeen().has(path);
}

/** Mark this event as seen. Idempotent. */
export function markFirstClickSeen(path: string): void {
  const seen = readSeen();
  if (seen.has(path)) return;
  seen.add(path);
  writeSeen(seen);
}

/** Test seam — wipes the seen-set. Not used outside tests. */
export function __resetFirstClickSeen(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
